import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { fmtTL, fmtNum } from "@/lib/utils";

export const EMPTY_LINE = { poz_no: "", description: "", unit: "", qty: "", unit_price: "" };

export function PriceBadge({ info: r, testid }) {
  if (!r) return <span className="text-[11px] text-muted-foreground" data-testid={testid}>Referans yok</span>;
  if (r.high) return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded ${r.diff_pct > 20 ? "bg-rose-500/15 text-rose-700 dark:text-rose-400" : "bg-amber-500/15 text-amber-700 dark:text-amber-400"}`}
      title={`Geçmiş ${r.count} fiyat: min ${fmtTL(r.min)} — max ${fmtTL(r.max)}`} data-testid={testid}>
      <AlertTriangle className="w-3 h-3" /> Ort. {fmtTL(r.avg)} · %{fmtNum(r.diff_pct)} yüksek
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
      title={`Geçmiş ${r.count} fiyat: min ${fmtTL(r.min)} — max ${fmtTL(r.max)}`} data-testid={testid}>
      <CheckCircle2 className="w-3 h-3" /> Ort. {fmtTL(r.avg)}
    </span>
  );
}

export default function HakedisLinesEditor({ lines, onChange, projectId, onRefs }) {
  const [refs, setRefs] = useState([]);
  const set = (i, patch) => onChange(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  useEffect(() => {
    const items = lines.filter(l => l.description && l.unit && Number(l.unit_price) > 0);
    if (!items.length) { setRefs([]); onRefs?.([]); return; }
    const t = setTimeout(() => {
      api.post("/analytics/price-check", {
        project_id: projectId || null,
        items: lines.map(l => ({ description: l.description || "", unit: l.unit || "", unit_price: Number(l.unit_price) || 0 })),
      }).then(r => { setRefs(r.data); onRefs?.(r.data); });
    }, 400);
    return () => clearTimeout(t);
  }, [JSON.stringify(lines.map(l => [l.description, l.unit, l.unit_price])), projectId]);

  return (
    <div className="space-y-2" data-testid="lines-editor">
      <div className="grid grid-cols-[1fr_90px_80px_90px_110px_110px_32px] gap-2 text-xs font-semibold text-muted-foreground px-1">
        <span>Tanım</span><span>Poz</span><span>Birim</span><span>Miktar</span><span>Birim Fiyat</span><span className="text-right">Tutar</span><span />
      </div>
      {lines.map((l, i) => (
        <div key={i} className="space-y-1">
          <div className="grid grid-cols-[1fr_90px_80px_90px_110px_110px_32px] gap-2 items-center">
            <DescriptionInput value={l.description} onChange={v => set(i, { description: v })}
              onPick={s => set(i, { description: s.description, unit: s.unit, poz_no: l.poz_no || s.poz_no, unit_price: l.unit_price || s.avg })} testid={`line-desc-${i}`} />
            <Input value={l.poz_no} onChange={e => set(i, { poz_no: e.target.value })} data-testid={`line-poz-${i}`} />
            <Input value={l.unit} onChange={e => set(i, { unit: e.target.value.toUpperCase() })} data-testid={`line-unit-${i}`} />
            <Input type="number" step="0.01" value={l.qty} onChange={e => set(i, { qty: e.target.value })} data-testid={`line-qty-${i}`} />
            <Input type="number" step="0.01" value={l.unit_price} onChange={e => set(i, { unit_price: e.target.value })} data-testid={`line-price-${i}`} />
            <span className="text-right font-mono-nums text-sm font-semibold">{fmtTL((Number(l.qty) || 0) * (Number(l.unit_price) || 0))}</span>
            <Button type="button" size="icon" variant="ghost" onClick={() => onChange(lines.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4 text-rose-500" /></Button>
          </div>
          {l.description && Number(l.unit_price) > 0 && <div className="pl-1"><PriceBadge info={refs[i]} testid={`line-price-badge-${i}`} /></div>}
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...lines, { ...EMPTY_LINE }])} data-testid="add-line-button">
        <Plus className="w-4 h-4 mr-1" /> Kalem Ekle
      </Button>
    </div>
  );
}

function DescriptionInput({ value, onChange, onPick, testid }) {
  const [opts, setOpts] = useState([]);
  const [focus, setFocus] = useState(false);

  useEffect(() => {
    if (!focus || (value || "").trim().length < 2) { setOpts([]); return; }
    const t = setTimeout(() => api.get("/analytics/price-reference", { params: { q: value } }).then(r => setOpts(r.data)), 300);
    return () => clearTimeout(t);
  }, [value, focus]);

  return (
    <div className="relative">
      <Input value={value} onChange={e => onChange(e.target.value)} onFocus={() => setFocus(true)} onBlur={() => setTimeout(() => setFocus(false), 200)}
        placeholder="İş kalemi ara veya yaz" data-testid={testid} />
      {focus && opts.length > 0 && (
        <div className="absolute z-50 mt-1 w-[560px] max-h-64 overflow-y-auto rounded-lg border border-border bg-popover shadow-lg" data-testid={`${testid}-options`}>
          {opts.map((o, k) => (
            <button key={k} type="button" onMouseDown={() => { onPick(o); setFocus(false); }}
              className="w-full text-left px-3 py-2 text-xs hover:bg-amber-500/10 border-b border-border last:border-0">
              <div className="font-medium">{o.description}</div>
              <div className="text-muted-foreground font-mono-nums">{o.unit} · ort. {fmtTL(o.avg)} · min {fmtTL(o.min)} · max {fmtTL(o.max)} · {o.count} fiyat</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
